import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * The API client.
 *
 * Two interceptors do most of the work:
 *
 *  - the request one attaches the bearer token and the active outlet
 *  - the response one turns HTTP status into application meaning, so no
 *    component ever string-matches an error message
 *
 * The status contract (mirrored in bootstrap/app.php):
 *   401  token gone      → clear session, bounce to /login with ?next=
 *   402  plan limit      → open the upgrade modal
 *   403  no capability   → toast
 *   404  not found       → also what another tenant's record returns
 *   409  totals mismatch → the POS re-syncs the cart
 *   422  validation      → typed, fed into react-hook-form
 *   429  throttled       → toast with the retry delay
 */

export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  // Bearer tokens, not cookies (ADR-004). Nothing needs credentialed requests.
  withCredentials: false,
  timeout: 30_000,
});

/* -------------------------------------------------------------------------- */
/* Typed errors                                                               */
/* -------------------------------------------------------------------------- */

export type ApiErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'validation_failed'
  | 'plan_limit_exceeded'
  | 'totals_mismatch'
  | 'insufficient_stock'
  | 'register_not_open'
  | 'conflict'
  | 'too_many_requests'
  | 'http_error'
  | 'network_error';

/*
 * Fields are declared explicitly rather than as constructor parameter
 * properties: `erasableSyntaxOnly` is on, so TypeScript-only syntax that
 * emits runtime code is banned. That setting is worth keeping — it means the
 * build stays a pure type-strip with no hidden transformation.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly payload: Record<string, unknown>;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    payload: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

/** 422 — carries per-field messages for react-hook-form. */
export class ValidationError extends ApiError {
  readonly errors: Record<string, string[]>;

  constructor(message: string, errors: Record<string, string[]>) {
    super(422, 'validation_failed', message, { errors });
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

/** 402 — the upgrade modal reads these fields directly. */
export class PlanLimitError extends ApiError {
  readonly limit: string;
  readonly current: number;
  readonly max: number | null;

  constructor(message: string, limit: string, current: number, max: number | null) {
    super(402, 'plan_limit_exceeded', message, { limit, current, max });
    this.name = 'PlanLimitError';
    this.limit = limit;
    this.current = current;
    this.max = max;
  }
}

/** 409 from POST /sales — the server recomputed a different total. */
export class TotalsMismatchError extends ApiError {
  readonly expected: number;
  readonly actual: number;

  constructor(message: string, expected: number, actual: number) {
    super(409, 'totals_mismatch', message, { expected, actual });
    this.name = 'TotalsMismatchError';
    this.expected = expected;
    this.actual = actual;
  }
}

/* -------------------------------------------------------------------------- */
/* Interceptor wiring                                                         */
/* -------------------------------------------------------------------------- */

type Handlers = {
  getToken: () => string | null;
  getOutletId: () => number | null;
  onUnauthenticated: () => void;
  onPlanLimit: (error: PlanLimitError) => void;
  onForbidden: (message: string) => void;
  onThrottled: (message: string, retryAfterSeconds: number) => void;
};

let handlers: Handlers | null = null;

/**
 * Called once at app boot from AuthProvider.
 *
 * Injected rather than imported so this module has no dependency on the
 * stores — which keeps it testable and avoids an import cycle between the
 * client and the auth state that the client populates.
 */
export function configureApi(next: Handlers): void {
  handlers = next;
}

api.interceptors.request.use((config) => {
  const token = handlers?.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // The outlet CAN come from a header (unlike the tenant) because one user
  // switches between shops in a single session. The server validates it
  // against outlet_user regardless — this names a preference, not a grant.
  const outletId = handlers?.getOutletId();
  if (outletId) config.headers['X-Outlet-Id'] = String(outletId);

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<Record<string, never>>) => {
    if (!error.response) {
      // No response at all: offline, DNS failure, or the request timed out.
      // Worth distinguishing, because the POS shows a different banner.
      return Promise.reject(
        new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.'),
      );
    }

    const { status, data } = error.response;
    const body = (data ?? {}) as Record<string, unknown>;
    const message = typeof body.message === 'string' ? body.message : 'Something went wrong.';

    switch (status) {
      case 401: {
        handlers?.onUnauthenticated();
        return Promise.reject(new ApiError(401, 'unauthenticated', message));
      }

      case 402: {
        const planError = new PlanLimitError(
          message,
          String(body.limit ?? ''),
          Number(body.current ?? 0),
          body.max === null || body.max === undefined ? null : Number(body.max),
        );
        handlers?.onPlanLimit(planError);
        return Promise.reject(planError);
      }

      case 403: {
        handlers?.onForbidden(message);
        return Promise.reject(new ApiError(403, 'forbidden', message));
      }

      case 409: {
        if (body.code === 'totals_mismatch') {
          return Promise.reject(
            new TotalsMismatchError(message, Number(body.expected ?? 0), Number(body.actual ?? 0)),
          );
        }
        return Promise.reject(new ApiError(409, 'conflict', message));
      }

      case 422: {
        return Promise.reject(
          new ValidationError(message, (body.errors ?? {}) as Record<string, string[]>),
        );
      }

      case 429: {
        const retryAfter = Number(error.response.headers?.['retry-after'] ?? 60);
        handlers?.onThrottled(message, retryAfter);
        return Promise.reject(new ApiError(429, 'too_many_requests', message));
      }

      default:
        return Promise.reject(
          new ApiError(status, (body.code as ApiErrorCode) ?? 'http_error', message, body),
        );
    }
  },
);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Unwrap the `{ data }` envelope every endpoint returns. */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<{ data: T }>(url, { params });
  return data.data;
}

/** For list endpoints, which also carry pagination in `meta`. */
export async function getPaginated<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ data: T[]; meta: PaginationMeta }> {
  const { data } = await api.get<{ data: T[]; meta: PaginationMeta }>(url, { params });
  return data;
}

export async function post<T>(
  url: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  const { data } = await api.post<{ data: T }>(url, body, { headers });
  return data.data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<{ data: T }>(url, body);
  return data.data;
}

export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<{ data: T }>(url);
  return data?.data;
}

export type PaginationMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
};

/**
 * Map a 422 onto react-hook-form fields.
 *
 * Laravel returns dotted paths ("lines.0.quantity") which is exactly what
 * react-hook-form expects for nested fields, so no translation is needed.
 */
export function applyServerErrors(
  error: unknown,
  setError: (field: string, error: { type: string; message: string }) => void,
): boolean {
  if (!(error instanceof ValidationError)) return false;

  for (const [field, messages] of Object.entries(error.errors)) {
    setError(field, { type: 'server', message: messages[0] });
  }

  return true;
}
